"use client";

import { Suspense } from "react";
import { Canvas } from "@react-three/fiber";

// 内聚的系统组件
import { LightingShadowSystem } from "./scene/LightingShadowSystem";
import { EnvironmentSystem } from "./scene/EnvironmentSystem";
import { CameraControls } from "./scene/CameraControls";
import { TextPointCloudObject } from "./crystal/TextPointCloudObject";
import { useCrystalCustomizer } from "@/contexts/CrystalCustomizerContext";

/**
 * 场景配置 - 组件内聚
 */
const SCENE_CONFIG = {
  camera: {
    position: [-4, 1, 4] as [number, number, number],
    fov: 28,
    near: 0.1,
    far: 1000,
  },
  performance: {
    antialias: true,
    alpha: true,
    maxPixelRatio: 2,
  },
};

/**
 * 工具函数
 */
const getDevicePixelRatio = () => {
  if (typeof window === "undefined") return 1;
  return Math.min(
    window.devicePixelRatio,
    SCENE_CONFIG.performance.maxPixelRatio,
  );
};

/**
 * CrystalPreview - 场景组装器
 * 负责Canvas设置和系统组件的组装，支持水晶材质类型选择
 */
export function CrystalPreview() {
  // 获取当前渲染模式
  const { config } = useCrystalCustomizer();
  const { renderMode } = config;
  return (
    <div className="w-full h-full flex items-center justify-center">
      <Canvas
        shadows
        camera={SCENE_CONFIG.camera}
        className="w-full h-full"
        dpr={getDevicePixelRatio()}
        performance={{ min: 0.5 }}
        gl={{
          antialias: SCENE_CONFIG.performance.antialias,
          alpha: SCENE_CONFIG.performance.alpha,
          powerPreference: "high-performance",
          stencil: false,
          depth: true,
        }}
      >
        {/* 环境系统 - 背景和HDR */}
        <EnvironmentSystem />

        {/* 统一光照阴影系统 - 确保光源和阴影参数完全一致 */}
        <LightingShadowSystem />

        <Suspense fallback={null}>
          {/* 文本点云对象 - 传递材质类型 */}
          <TextPointCloudObject materialType={renderMode} />

          {/* 相机控制 */}
          <CameraControls />
        </Suspense>
      </Canvas>
    </div>
  );
}
